// Subscription mode: a page-level toggle that reveals subscription boxes on product
// cards, a delivery-frequency dropdown that updates the displayed price, and an AJAX
// add-to-cart that attaches the chosen selling plan and refreshes the theme cart drawer.

const STORAGE_KEY = 'drmb:subscription-mode';

function setActive(on) {
  document.documentElement.classList.toggle('subscription-active', on);
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? null : v === '1';
  } catch (e) {
    return null;
  }
}

function writeStored(on) {
  try {
    localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch (e) {
    /* ignore */
  }
}

function initToggles(root) {
  const toggles = root.querySelectorAll('[data-sub-toggle]');
  if (!toggles.length) return;

  let stored = readStored();
  let on = stored === null ? false : stored;

  // If nothing stored yet, honor a toggle's "on by default" setting.
  if (stored === null) {
    toggles.forEach((t) => {
      const host = t.closest('subscription-toggle');
      if (host && host.dataset.default === 'true') on = true;
    });
  }

  setActive(on);

  toggles.forEach((toggle) => {
    toggle.checked = on;
    if (toggle.dataset.subBound === 'true') return;
    toggle.dataset.subBound = 'true';

    toggle.addEventListener('change', () => {
      const next = toggle.checked;
      setActive(next);
      writeStored(next);
      // Keep any other toggles on the page in sync.
      document.querySelectorAll('[data-sub-toggle]').forEach((other) => {
        other.checked = next;
      });
    });
  });
}

function initSelects(root) {
  root.querySelectorAll('[data-sub-select]').forEach((select) => {
    if (select.dataset.subBound === 'true') return;
    select.dataset.subBound = 'true';

    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const box = select.closest('.subscription-option__box');
      if (!box) return;

      const priceEl = box.querySelector('[data-sub-price]');
      const saveEl = box.querySelector('[data-sub-save]');
      const planInput = box.querySelector('[data-sub-plan]');

      if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price;
      if (saveEl && option.dataset.save) saveEl.textContent = option.dataset.save;
      if (planInput) planInput.value = option.value;
    });
  });
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

function dispatchCartUpdate(cart, sections, didError = false) {
  // Mirrors the theme's CartAddEvent (event name "cart:update") so the cart drawer,
  // cart items, and cart bubble update. The drawer auto-opens unless didError is set.
  const event = new Event('cart:update', { bubbles: true });
  event.detail = {
    resource: cart || undefined,
    sourceId: 'subscription-option',
    data: {
      source: 'subscription-option',
      sections: sections,
      itemCount: cart && cart.item_count,
      didError: didError,
    },
  };
  document.dispatchEvent(event);
}

/**
 * @param {HTMLFormElement} form
 * @param {string} [message]
 */
function showError(form, message) {
  const errorEl = /** @type {HTMLElement | null} */ (form.querySelector('[data-sub-error]'));
  if (!errorEl) return;
  errorEl.textContent = message || errorEl.dataset.fallback || '';
  errorEl.hidden = false;
}

/** @param {HTMLFormElement} form */
function hideError(form) {
  const errorEl = /** @type {HTMLElement | null} */ (form.querySelector('[data-sub-error]'));
  if (errorEl) errorEl.hidden = true;
}

function initForms(root) {
  root.querySelectorAll('[data-sub-form]').forEach((form) => {
    if (form.dataset.subBound === 'true') return;
    form.dataset.subBound = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.subAdding === 'true') return;
      form.dataset.subAdding = 'true';
      hideError(form);

      const formData = new FormData(form);
      const ids = cartSectionIds();
      if (ids.length) formData.append('sections', ids.join(','));

      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          // e.g. 422 — Shopify returns an error object with `status`.
          console.error('[subscription] add to cart failed', data);
          showError(form, data.description || data.message);
          // Shopify may still have added up to the max quantity: refresh without opening the drawer.
          dispatchCartUpdate(null, undefined, true);
          return;
        }

        let cart = null;
        try {
          cart = await (await fetch('/cart.js')).json();
        } catch (e) {
          /* item_count is best-effort */
        }

        // The cart:update listener in layout/theme.liquid refreshes and opens the drawer.
        dispatchCartUpdate(cart, data.sections);
      } catch (error) {
        console.error('[subscription] add to cart error', error);
        showError(form);
      } finally {
        form.dataset.subAdding = 'false';
      }
    });
  });
}

// Show a fixed bottom bar once the inline toggle scrolls out of view.
function initSticky() {
  const stickies = document.querySelectorAll('[data-sub-sticky]');
  if (!stickies.length) return;

  // Keep only the most recent sticky bar (guards against editor re-renders) and move it
  // to <body> so position: fixed is reliable regardless of transformed ancestors.
  const sticky = stickies[stickies.length - 1];
  stickies.forEach((el) => {
    if (el !== sticky) el.remove();
  });

  const inline = document.querySelector('subscription-toggle.subscription-toggle');
  if (!inline) return;

  if (sticky.parentElement !== document.body) {
    document.body.appendChild(sticky);
  }

  if (sticky.dataset.subObserved === 'true') return;
  sticky.dataset.subObserved = 'true';

  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;
      const offscreen = !entry.isIntersecting;
      sticky.classList.toggle('is-visible', offscreen);
      sticky.setAttribute('aria-hidden', offscreen ? 'false' : 'true');
    },
    { threshold: 0 }
  );
  observer.observe(inline);
}

function init(root = document) {
  initToggles(root);
  initSelects(root);
  initForms(root);
  initSticky();
}

init();

// Re-init when sections are re-rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  init(event.target instanceof HTMLElement ? event.target : document);
});
